import { useCallback, useEffect, useMemo } from 'react';

import { useLocalStorage } from '@/shared/hooks/useLocalStorage';

import type { RedemptionState } from '../types';

/**
 * 영속화 키 (v3).
 *
 * v3는 서버 스키마와 1:1 정렬된 단일 카운트 모델로 전환되었다 (스트리머 A/B/C 분류 제거,
 * 검증 코드 목록 제거). 구버전 키(v1, v2)는 진입 시 자동 정리되어 사용자 디바이스에
 * 잔존 데이터를 남기지 않는다.
 */
const STORAGE_KEY = 'darak-kidday/redemption-state-v3';

/** 사용자 디바이스 청소 대상 — 구버전 키들. */
const LEGACY_STORAGE_KEYS = [
  'darak-kidday/redemption-state-v1',
  'darak-kidday/redemption-state-v2',
];

/** 보상 다운로드 활성화 임계값 — 서버 정책과 동일하게 3개. */
const COMPLETE_THRESHOLD = 3;

const INITIAL: RedemptionState = {
  userCode: null,
  codeCount: 0,
  downloadUrl: null,
};

/**
 * 코드 입력 흐름 상태 훅 (v3 — 단일 카운트 모델).
 *
 * - 유저 코드(`userCode`), 누적 카운트(`codeCount`), 보상 URL(`downloadUrl`)을
 *   localStorage에 영속화 (새로고침/탭 복원 안전).
 * - 새로고침 시 `useLocalStorage` 가 동기 초기화되므로 깜빡임 없이 즉시 로그인 상태가 복원된다.
 * - 서버 응답으로 카운트가 갱신되며 클라이언트 측 비즈니스 로직은 두지 않는다.
 * - 3/3 도달 여부는 `codeCount` 만으로 판정한다.
 */
export function useRedemptionState(): {
  state: RedemptionState;
  /** 3/3 모두 등록되었는지 (서버 카운트 기준). */
  isComplete: boolean;
  /** 유저 코드를 발급/복원했는지. false면 코드 입력 UI를 비활성화한다. */
  hasUserCode: boolean;
  /** 유저 코드를 설정 (발급 직후 또는 부팅 복원 시 호출). 카운트는 보존된다. */
  setUserCode: (code: string) => void;
  /**
   * 서버에서 받은 진행 상태로 통째로 교체한다.
   * 다른 디바이스 코드로 "이어서 하기" 또는 부팅 시 status 동기화에 사용된다.
   */
  replaceFromServer: (params: {
    userCode: string;
    codeCount: number;
    downloadUrl: string | null;
  }) => void;
  /** 서버가 응답한 누적 카운트와 다운로드 URL을 갱신 (코드 등록 성공 시). */
  applyStatus: (params: { codeCount: number; downloadUrl: string | null }) => void;
  /** 전체 진행을 초기화 (디버그/테스트용). 유저 코드까지 모두 삭제된다. */
  reset: () => void;
} {
  const [state, setState] = useLocalStorage<RedemptionState>(STORAGE_KEY, INITIAL);

  // 구버전 키들을 한 번만 정리한다.
  // v1/v2 시기에 누적해둔 검증 코드/스트리머 정보는 새 모델과 호환되지 않으므로,
  // 페이지 진입 시 자연스럽게 유저 코드 자동 발급 흐름을 타도록 깨끗이 제거한다.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    for (const key of LEGACY_STORAGE_KEYS) {
      try {
        if (window.localStorage.getItem(key) !== null) {
          window.localStorage.removeItem(key);
        }
      } catch {
        // 시크릿 모드 등 — 무시
      }
    }
  }, []);

  const isComplete = useMemo(
    () => state.codeCount >= COMPLETE_THRESHOLD,
    [state.codeCount],
  );
  const hasUserCode = state.userCode !== null && state.userCode !== '';

  const setUserCode = useCallback(
    (code: string) => {
      setState((prev) => ({ ...prev, userCode: code }));
    },
    [setState],
  );

  const replaceFromServer = useCallback(
    (params: {
      userCode: string;
      codeCount: number;
      downloadUrl: string | null;
    }) => {
      setState({
        userCode: params.userCode,
        codeCount: params.codeCount,
        downloadUrl: params.downloadUrl,
      });
    },
    [setState],
  );

  const applyStatus = useCallback(
    (params: { codeCount: number; downloadUrl: string | null }) => {
      setState((prev) => ({
        ...prev,
        codeCount: params.codeCount,
        downloadUrl: params.downloadUrl,
      }));
    },
    [setState],
  );

  const reset = useCallback(() => {
    setState(INITIAL);
  }, [setState]);

  return {
    state,
    isComplete,
    hasUserCode,
    setUserCode,
    replaceFromServer,
    applyStatus,
    reset,
  };
}
